// Component copy to CreateScheduledTournamentScreen.tsx on create-scheduled-tournament
import 'react-native-get-random-values'

import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { debounce } from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { Keyboard, KeyboardAvoidingView, ScrollView } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { TouchableOpacity } from 'react-native-gesture-handler'
import { useLocalSearchParams, useRouter } from 'expo-router'

import { DS } from '../../design-system'
import { useCreateGame } from '../../hooks/use-create-game'
import { translate } from '../../i18n'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { GameModeTag, TournamentAccessControl, TournamentDifficulty } from '../../models/api-store'
import { InputEditUserGameSchedule } from '../../models/api-store/RootStore.base'
import { DeleteBottomSheetModal } from './DeleteBottomSheetModal'
import { CreateTournamentForm } from './CreateTournamentForm'
import { styles } from './styles'
import { PressableWrapper } from './pressable-wrapper'
import { WebWrapper } from '@/components/WebWrapper'

import { useTournamentConfigStore } from '@/screens/create-game/UserRoleScreen'

export const MAX_NAME_LENGTH = 20

export const CreateTournamentScreen = observer(() => {
  const router = useRouter()

  const {
    game: {
      actionTime,
      blindTime,
      setActionTime,
      setGameKind,
      setRemainingTime,
      tournamentAccessControl,
      userGameScheduleFullResponse,
      gameScheduledAt,
      createDeleteScheduleSheet,
      getYourScheduledGames
    },
    session: { isTeacher, selectedDailyTask, updateDailyTaskStatus },
    analytics
  } = useStore()
  const { accessControl, isEdit } = useLocalSearchParams<{ accessControl: TournamentAccessControl; isEdit: string }>()
  const currentAccessControl = accessControl ?? tournamentAccessControl

  const [tournamentName, setTournamentName] = React.useState('')
  const [tournamentBlindsLength, setTournamentBlindsLength] = React.useState('')
  const [tournamentStartingChips, setTournamentStartingChips] = React.useState('')
  const [tournamentActionTime, setTournamenActionTime] = React.useState('')
  const [scheduleForLater, setScheduleForLater] = React.useState(false)
  const [tournamentDifficulty, setTournamentDifficulty] = React.useState(TournamentDifficulty.GENERAL)
  const [errorMsg, setErrorMsg] = React.useState('')
  const [nameTooLong, setNameTooLong] = React.useState(false)
  const [isNameEmpty, setIsNameEmpty] = React.useState(false)
  const scrollViewRef = React.useRef<ScrollView>(null)
  const { loading, createGame, editScheduledGame } = useCreateGame()
  const isHost = userGameScheduleFullResponse?.isHost

  const handleAction = async () => {
    if (!tournamentName) {
      setErrorMsg('Game name required')
      setIsNameEmpty(true)
      return
    }
    if (tournamentName.length > MAX_NAME_LENGTH) {
      setNameTooLong(true)
      return
    }
    if (isEdit) {
      await editGame()
    } else {
      await createActiveTournament()
    }
  }

  const createActiveTournament = async () => {
    if (!tournamentName) {
      setErrorMsg('Game name required')
      setIsNameEmpty(true)
      return
    }
    const isPrivateTournament = currentAccessControl === TournamentAccessControl.private

    const tournamentConfig = {
      tournamentName,
      tournamentBlindsLength,
      tournamentActionTime,
      tournamentStartingChips,
      tournamentDifficulty,
      accessControl: currentAccessControl
    }

    try {
      if (selectedDailyTask && selectedDailyTask.category.toLowerCase() === 'play') {
        updateDailyTaskStatus()
      }
      if (isTeacher && isPrivateTournament && !scheduleForLater) {
        useTournamentConfigStore.setState({
          isPrivateTournament,
          ...tournamentConfig
        })
        router.navigate(`/game/create/user-role?gameKind=${GameModeTag.tournament}`)
      } else {
        setGameKind(GameModeTag.tournament)
        setRemainingTime(tournamentBlindsLength ? Number(tournamentBlindsLength) * 60 : blindTime)
        setActionTime(tournamentActionTime ? Number(tournamentActionTime) * 1000 : actionTime)
        await createGame(
          {
            gameKind: GameModeTag.tournament,
            accessControl: currentAccessControl,
            scheduleForLater,
            scheduleForLaterAt: gameScheduledAt
          },
          tournamentConfig
        )
        await getYourScheduledGames()
      }
    } catch (error) {
      console.error(error)
    }
  }

  const editGame = async () => {
    if (!userGameScheduleFullResponse?.hash) return
    const payload: InputEditUserGameSchedule = {
      actionTime: Number(tournamentActionTime),
      blindTimer: Number(tournamentBlindsLength),
      name: tournamentName,
      startingChips: Number(tournamentStartingChips)
    }
    analytics.setEvent(events.game.shedule_game_edited, {
      game_name: tournamentName,
      action_time: Number(tournamentActionTime),
      blind_time: Number(tournamentBlindsLength),
      starting_chips: Number(tournamentStartingChips)
    })
    await editScheduledGame({ hash: userGameScheduleFullResponse.hash, payload })
  }

  const onBack = () => {
    if (router.canDismiss()) {
      return router.dismiss()
    }
    if (accessControl === TournamentAccessControl.private) {
      return router.dismissTo('/game/create/new')
    }
    router.dismissTo('/(root)/(tabs)/play')
  }

  const triggerDeleteScheduleSheet = () => {
    createDeleteScheduleSheet()
  }

  useEffect(() => {
    if (!isEdit) {
      setTournamentName('')
      setTournamentBlindsLength('')
      setTournamentStartingChips('')
      setTournamenActionTime('')
      setErrorMsg('')
      setTournamentDifficulty(TournamentDifficulty.GENERAL)
      setNameTooLong(false)
    } else if (userGameScheduleFullResponse) {
      setTournamentName(userGameScheduleFullResponse.name ?? '')
      setTournamentBlindsLength(userGameScheduleFullResponse.blindTimer?.toString() ?? '')
      setTournamentStartingChips(userGameScheduleFullResponse.startingChips?.toString() ?? '')
      setTournamenActionTime(userGameScheduleFullResponse?.actionTime?.toString() ?? '')
    }
  }, [userGameScheduleFullResponse, isEdit])

  useEffect(() => {
    if (scheduleForLater) {
      scrollViewRef?.current?.scrollToEnd({ animated: true })
    }
  }, [scheduleForLater])

  return (
    <KeyboardAvoidingView style={styles.mainContainer}>
      <StatusBar animated={true} style={'light' as StatusBarStyle} hidden={false} />
      <DS.Frame style={[styles.safeArea, styles.container]}>
        <WebWrapper style={styles.container}>
          <SafeAreaView style={styles.safeArea}>
            <DS.Frame style={styles.headerContainer}>
              <TouchableOpacity onPress={onBack}>
                <DS.Icon name="Arrow---left" size={24} color={'Global/White'} />
              </TouchableOpacity>
              {isEdit && isHost && (
                <DS.Text variant="Body Medium/Medium" color={'Global/White'} onPress={triggerDeleteScheduleSheet}>
                  Delete Game
                </DS.Text>
              )}
            </DS.Frame>
            <ScrollView showsVerticalScrollIndicator={false} ref={scrollViewRef}>
              <PressableWrapper onPress={() => Keyboard.dismiss()}>
                <DS.Frame gap="24">
                  <DS.Text variant="Heading 1/Medium" color="Brand/Blue">
                    {isEdit ? translate('createTournamentScreen.editTitle') : translate('createTournamentScreen.title')}
                  </DS.Text>
                  <CreateTournamentForm
                    isNameEmpty={isNameEmpty}
                    setIsNameEmpty={setIsNameEmpty}
                    tournamentName={tournamentName}
                    nameTooLong={nameTooLong}
                    tournamentBlindsLength={tournamentBlindsLength}
                    tournamentStartingChips={tournamentStartingChips}
                    tournamentActionTime={tournamentActionTime}
                    setTournamentName={setTournamentName}
                    setTournamentBlindsLength={setTournamentBlindsLength}
                    setTournamentStartingChips={setTournamentStartingChips}
                    setTournamenActionTime={setTournamenActionTime}
                    setScheduleForLater={setScheduleForLater}
                    scheduleForLater={scheduleForLater}
                    isEdit={!!isEdit}
                  />
                </DS.Frame>
              </PressableWrapper>
            </ScrollView>
            <DS.Frame style={styles.buttonContainer}>
              <DS.Button
                type="On Dark"
                variant="Large/Primary"
                disabled={loading}
                onPress={debounce(handleAction, 200)}
              >
                {isEdit
                  ? translate('createTournamentScreen.editButton')
                  : translate('createTournamentScreen.createButton')}
              </DS.Button>
              <DS.Text variant="Caption/Regular" style={styles.errorMsg} color="Global/White">
                {errorMsg}
              </DS.Text>
            </DS.Frame>
          </SafeAreaView>
        </WebWrapper>
      </DS.Frame>
      <DeleteBottomSheetModal />
    </KeyboardAvoidingView>
  )
})
